import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ThemeService } from './theme.service';

// Colors the status bar to match the app's header instead of leaving it a default gray,
// WITHOUT edge-to-edge (overlaysWebView) — that was tried and reverted on Android: it raced
// against windowSoftInputMode's resize calculation on first keyboard focus, leaving forms
// stuck unresponsive until a screen lock/unlock forced Android to reattach window focus. This
// non-overlay version gets the color-matching without touching decor-fits-system-windows, so
// there's no edge-to-edge layout and no race — the window still resizes normally for the
// keyboard. Colors mirror header.component.css's light/dark background (rgba(255,255,255,.88)
// / rgba(24,24,27,.88)).
//
// Also stamps `platform-android`/`platform-ios` on <html> — every platform-only visual tweak
// elsewhere in the app (dashboard responsive layout, table/chart overflow fixes, safe-area
// insets, etc.) is scoped under `html.platform-android`/`html.platform-ios` or
// `:host-context(...)` of the same, so none of it leaks onto the desktop web app or the other
// native platform.
@Injectable({ providedIn: 'root' })
export class StatusBarService {
  private readonly themeService = inject(ThemeService);

  constructor() {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      document.documentElement.classList.add('platform-android');
    } else if (platform === 'ios') {
      document.documentElement.classList.add('platform-ios');
    } else {
      return;
    }
    this.applyForTheme(this.themeService.isDark);
    this.themeService.isDark$.subscribe((isDark) => this.applyForTheme(isDark));
  }

  private applyForTheme(isDark: boolean): void {
    // 2026-10-02 iPhone: the strip behind the status bar is NOT painted by our page (contentInset 'automatic' keeps the page below it), iOS paints it
    // with the PHONE's own appearance (black in dark mode, light grey in light mode). The text colour used to follow the APP theme, so a Light app
    // theme on a phone in dark mode gave dark text on a black strip: the clock, signal and Wi-Fi vanished (only the yellow battery badge showed).
    // Style.Default follows the device appearance, i.e. the same thing that colours the strip, so the text can never disappear. Android keeps the
    // theme-based style and colour below (it paints its own bar).
    // 2026-10-10: the iPhone app now overlays the web view and the header paints the strip behind the clock in the APP theme colour (white or dark),
    // so the clock text must follow the app theme too (Style.Dark = light text for a dark strip). Style.Default followed the phone and would put dark
    // text on a dark strip when the phone is light and the app is dark.
    StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light }).catch(() => {});
    // 2026-10-10: setBackgroundColor is implemented on iOS in @capacitor/status-bar 8 (StatusBar.swift: with overlaysWebView false the plugin moves
    // the web view below the status bar and paints a status-bar-sized view behind the clock in this colour). The old note here said it was a
    // no-op on iOS; that was out of date. White in Light and the header's dark colour in Dark, the same two colours as the header
    // (header.component.css, platform-ios).
    StatusBar.setBackgroundColor({ color: isDark ? '#18181b' : '#ffffff' }).catch(() => {});
  }
}
